import { useEffect } from "react";

import { Link, useHistory } from "react-router-dom";

import { Alert, Button, ConfigProvider, Flex, Form, Input, Typography } from "antd";

import { useTranslation } from "react-i18next";

import customizeRequiredMark from "../../components/custom_antd/CustomizeRequiredMark";
import { useUser } from "../../contexts/UserContext/UserContext";
import useLogin from "./hooks/useLogin";
import useLoginQueryParams from "./hooks/useLoginQueryParams";
import { normalizeEmail } from "./utils/normalizeEmail";

const { Paragraph, Text, Title } = Typography;

const Login = () => {
    const [form] = Form.useForm();
    const { t } = useTranslation("login");
    const history = useHistory();
    const { state } = useUser();
    const { login, loading, error, setError } = useLogin();

    useLoginQueryParams(form, login);

    useEffect(() => {
        if (state.isAuthenticated) {
            history.push("/");
        }
    }, [state.isAuthenticated, history]);

    const handleFinish = (values) => {
        login({
            email: values.email,
            password: values.password,
        });
    };

    return (
        <div className="flex min-h-screen w-full bg-white">
            <aside
                className="relative hidden w-[44%] shrink-0 flex-col justify-between overflow-hidden bg-[#5D5DF6] px-10 py-12 text-white lg:flex xl:px-14">
                <div className="pointer-events-none absolute -right-24 -top-24 h-72 w-72 rounded-full bg-white/10" />
                <div className="pointer-events-none absolute -bottom-32 -left-16 h-80 w-80 rounded-full bg-white/10" />

                <img
                    src="/logos/Logo.png"
                    alt="SoftCar"
                    className="relative z-10 h-8 w-auto max-w-[180px] brightness-0 invert"
                />

                <div className="relative z-10 max-w-md">
                    <Title level={2} className="!mb-3 !font-bold !text-white">
                        {t("welcome")}
                    </Title>
                    <Paragraph className="!mb-0 !text-base !text-white/85">{t("loginToAccount")}</Paragraph>
                </div>

                <Text className="relative z-10 text-sm text-white/70">
                    © SoftCar | {new Date().getFullYear()}
                </Text>
            </aside>

            <main className="flex min-h-screen flex-1 flex-col bg-[#F2F2FA] lg:bg-white">
                <Flex vertical align="center" justify="center" className="flex-1 px-6 py-10 sm:px-10">
                    <ConfigProvider
                        theme={{
                            token: {
                                colorPrimary: "#5D5DF6",
                                borderRadius: 8,
                            },
                        }}>
                        <div className="w-full max-w-[400px]">
                            <div className="mb-8 text-center lg:text-left">
                                <img
                                    src="/logos/Logo.png"
                                    alt="SoftCar"
                                    className="mx-auto mb-6 h-8 w-auto max-w-[180px] object-contain lg:mx-0 lg:hidden"
                                />
                                <Title level={3} className="!mb-1 !mt-0 !font-semibold !text-[#2F2F46]">
                                    {t("login")}
                                </Title>
                                <Text type="secondary">{t("loginToAccount")}</Text>
                            </div>

                            {error ? (
                                <Alert
                                    className="mb-6"
                                    type="error"
                                    message={error}
                                    showIcon
                                    closable
                                    onClose={() => setError("")}
                                />
                            ) : null}

                            <Form
                                form={form}
                                layout="vertical"
                                requiredMark={customizeRequiredMark}
                                onFinish={handleFinish}
                                autoComplete="on">
                                <Form.Item
                                    name="email"
                                    label={t("email")}
                                    normalize={normalizeEmail}
                                    rules={[
                                        { required: true, message: t("enterEmail") },
                                        { type: "email", message: t("invalidEmail") },
                                    ]}>
                                    <Input
                                        placeholder={t("emailPlaceholder")}
                                        autoComplete="email"
                                        autoCapitalize="none"
                                        autoCorrect="off"
                                        spellCheck={false}
                                        size="large"
                                    />
                                </Form.Item>

                                <Form.Item
                                    name="password"
                                    label={t("password")}
                                    rules={[{ required: true, message: t("enterPassword") }]}>
                                    <Input.Password
                                        placeholder={t("passwordPlaceholder")}
                                        autoComplete="current-password"
                                        size="large"
                                    />
                                </Form.Item>

                                <Form.Item className="!mb-4">
                                    <Button type="primary" htmlType="submit" loading={loading} block size="large">
                                        {loading ? t("loggingIn") : t("login")}
                                    </Button>
                                </Form.Item>

                                <Flex justify="center" className="lg:justify-start">
                                    <Link to="/forgotpassword" className="text-[#5D5DF6] hover:text-[#4a4ae0]">
                                        {t("forgotPassword")}
                                    </Link>
                                </Flex>
                            </Form>
                        </div>
                    </ConfigProvider>
                </Flex>

                <Text type="secondary" className="pb-6 text-center text-sm lg:hidden">
                    © SoftCar | {new Date().getFullYear()}
                </Text>
            </main>
        </div>
    );
};

export default Login;
